'use client';

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { TestFormData } from '@/types/test';
import { SubmissionFormData } from '@/types/submission';
import { apiClient } from '@/lib/api-client';

// 初期値の設定
const initialFormData: Partial<TestFormData & SubmissionFormData> = {
  alwaysOn: 'off',
  nops: '利用しない',
  lteAops: '利用しない',
  ipOps: '利用しない',
  clap: '利用しない',
  // 新しいフォーム用の初期値
  always_on_select: 'off',
  nops_permission: '利用しない',
  lte_ops_permission: '利用しない',
  ip_ops_permission: '利用しない',
  clap_permission: '利用しない',
  vm_type: 'スタンダードタイプ',
  // division_idは後でAPIから取得するので初期値は空文字列に
  division_id: '',
};

// Context の型定義
type FormContextType = {
  formData: Partial<TestFormData & SubmissionFormData>;
  setFormData: (data: Partial<TestFormData & SubmissionFormData>) => void;
  updateFormData: (data: Partial<TestFormData & SubmissionFormData>) => void;
  resetFormData: () => void;
  clearFormDataForNewSubmission: () => void;
  loading: boolean;
  submissionType: string;
  setSubmissionType: (type: string) => void;
};

// Context の作成
const FormContext = createContext<FormContextType | undefined>(undefined);

// Provider コンポーネント
export function FormProvider({ children }: { children: ReactNode }) {
  const [formData, setFormData] = useState<Partial<TestFormData & SubmissionFormData>>(initialFormData);
  const [loading, setLoading] = useState<boolean>(true);
  const [submissionType, setSubmissionType] = useState<string>('new');

  // 初回マウント時にユーザー情報を取得
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await apiClient.getCurrentUser();

        if (response.success && response.data) {
          // APIから取得した組織IDをフォームデータに設定
          // division.idを優先して使用
          const divisionId = response.data?.division?.id || '';
          setFormData(prev => ({
            ...prev,
            division_id: divisionId
          }));
        } else {
          console.error('ユーザー情報の取得に失敗しました:', response.error || 'データが取得できませんでした');
        }
      } catch (error) {
        console.error('ユーザー情報の取得中にエラーが発生しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // データを更新する関数
  const updateFormData = (newData: Partial<TestFormData & SubmissionFormData>) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  // データをリセットする関数
  const resetFormData = () => {
    // リセット時にはAPIから取得した部署IDを保持するため、現在のdivision_idを保存
    const currentDivisionId = formData.division_id;
    setFormData({ ...initialFormData, division_id: currentDivisionId || '' });
  };

  // 新規申請時の自動クリア関数
  const clearFormDataForNewSubmission = () => {
    // 新規申請時は完全にクリアし、division_idのみ保持
    const currentDivisionId = formData.division_id;
    console.log('新規申請のためFormContextをクリアします。division_id:', currentDivisionId);

    // 初期値にdivision_idを追加してセット
    const clearedData = {
      ...initialFormData,
      division_id: currentDivisionId || ''
    };

    console.log('クリア後のデータ:', clearedData);
    setFormData(clearedData);
  };

  return (
    <FormContext.Provider value={{
      formData,
      setFormData,
      updateFormData,
      resetFormData,
      clearFormDataForNewSubmission,
      loading,
      submissionType,
      setSubmissionType
    }}>
      {children}
    </FormContext.Provider>
  );
}

// カスタムフックの作成
export function useFormContext() {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}
